import {
  ProjectArticle,
  ProjectGallery,
  ProjectHighlightCards,
  ProjectLearningGrid,
  ProjectNextSteps,
  ProjectProcessTimeline,
  ProjectToolPills,
} from "../ProjectArticle";
import { getProjectSummary } from "../projectIndex";

const project = getProjectSummary("3")!;

function SystemDiagram() {
  return (
    <section>
      <h2 className="mb-8">How The System Fits Together</h2>
      <div className="mx-auto max-w-5xl overflow-hidden">
        <img
          src="/assets/images/projects/gyro-stabilised-spoon-system-infographic.png"
          alt="Diagram showing shaky hand movement, MPU-6050 IMU readings, BBC micro:bit control, filtering, servo correction, and a steadier spoon"
          className="block w-full h-auto"
        />
      </div>
    </section>
  );
}

export default function GyroStabilisedSpoon() {
  return (
    <ProjectArticle project={project}>
      <p>
        This project is a spoon that tries to stay level when the person holding
        it has shaky hands. I started thinking about it because eating is such a
        normal thing, but for people with tremors it can become frustrating,
        messy, and embarrassing. I wanted to see if a small robot spoon could
        help keep the food steadier.
      </p>

      <p>
        My first test was not elegant at all. I taped a normal spoon onto a DJI
        camera gimbal just to find out if active stabilisation could work for
        eating. It looked strange and was much too heavy, but it proved the idea
        was worth trying. After that, I moved to a smaller prototype using a BBC
        micro:bit, an MPU-6050 motion sensor, and two servo motors.
      </p>

      <SystemDiagram />

      <ProjectHighlightCards
        challenge={
          <ul>
            <li>Keep the spoon level without fighting every normal hand movement.</li>
            <li>Read noisy sensor data fast enough for real-time correction.</li>
            <li>Stop the servos from jittering or overcorrecting the spoon angle.</li>
            <li>Power the servos safely without resetting the micro:bit.</li>
            <li>Make the mechanism feel lighter and less awkward to hold.</li>
          </ul>
        }
        result={
          <ul>
            <li>The taped DJI gimbal version quickly proved that stabilisation could help.</li>
            <li>The custom prototype used a micro:bit, an IMU, and a 2-axis servo frame.</li>
            <li>The spoon could react to tilt and try to correct its pitch and roll.</li>
            <li>It became a working experiment, but not yet a finished medical device.</li>
          </ul>
        }
      />

      <ProjectProcessTimeline
        items={[
          {
            step: "Try the fastest proof of concept",
            detail:
              "I taped a spoon to a DJI camera gimbal. It was a bit ridiculous, but it immediately showed that stabilising a spoon was possible.",
          },
          {
            step: "Build a smaller robot version",
            detail:
              "I switched to a BBC micro:bit, an MPU-6050 sensor, two MG90S servos, and a simple 2-axis frame so the spoon could move by itself.",
          },
          {
            step: "Measure the shaking",
            detail:
              "The IMU gave accelerometer and gyroscope readings. I used them to estimate the spoon's tilt instead of guessing what the hand was doing.",
          },
          {
            step: "Smooth the sensor readings",
            detail:
              "Raw sensor values jump around a lot, so I experimented with complementary filtering, dead zones, and limits to calm the movement down.",
          },
          {
            step: "Tune the servo correction",
            detail:
              "A simple PD control loop moved the servos in the opposite direction of the unwanted tilt. Too much gain made it wobble, so tuning mattered.",
          },
          {
            step: "Fix the practical problems",
            detail:
              "The servos needed their own regulated 5V power supply and a common ground. I also had to think about balance, weight, and vibration.",
          },
        ]}
      />

      <ProjectLearningGrid
        items={[
          "Assistive technology is not just about making something technically work. It also has to feel comfortable and respectful for the person using it.",
          "Mechanical design matters as much as code. If the spoon is too heavy or badly balanced, the software cannot magically fix everything.",
          "Sensor readings are messy in real life. Filtering and tuning are just as important as reading the numbers.",
          "Servo motors can pull a lot of current. I learned why separate power and common grounding are important.",
          "An ugly first prototype can be very useful because it answers the biggest question quickly: is this idea worth continuing?",
          "Small inventions can connect robotics, healthcare, electronics, and human feelings all at the same time.",
        ]}
      />

      <ProjectToolPills
        tools={[
          "BBC micro:bit",
          "MPU-6050 IMU",
          "MG90S servos",
          "2-axis gimbal frame",
          "Regulated 5V UBEC",
          "USB power bank",
          "Microsoft MakeCode",
          "MicroPython",
          "I2C",
          "Complementary filter",
          "PD control loop",
          "DJI gimbal test",
        ]}
      />

      <ProjectGallery
        images={[
          {
            src: "/assets/images/projects/gyro-stabilised-spoon-prototype-setup.jpg",
            alt: "Looking for electronic parts for the gyro-stabilised spoon prototype",
            fit: "contain",
          },
          {
            src: "/assets/images/projects/gyro-stabilised-spoon-electronics.jpg",
            alt: "Electronics components wall used while choosing sensors and modules",
            fit: "contain",
          },
        ]}
      />

      <ProjectNextSteps
        items={[
          {
            title: "Make it lighter",
            detail:
              "The handle and spoon mount need to be smaller, better balanced, and more comfortable to hold.",
          },
          {
            title: "Try smoother motors",
            detail:
              "Brushless gimbal motors could make the motion quieter, smoother, and more precise than hobby servos.",
          },
          {
            title: "Improve tremor detection",
            detail:
              "A future version could learn the difference between normal slow movement and fast tremor movement.",
          },
          {
            title: "Design for real use",
            detail:
              "The next prototype should think about food-safe parts, cleaning, grip shape, and feedback from people who understand assistive devices.",
          },
        ]}
      />
    </ProjectArticle>
  );
}
